<template>
  <div class="gallery-view">
    <v-container>
      <v-row>
        <v-col cols="12" class="d-flex align-center">
          <div>
            <h1 class="text-h4 mb-1">Gallery</h1>
            <p class="text-subtitle-1 gallery-subtitle mb-0">
              Your enhanced images.
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn color="primary" to="/">
            <v-icon left small>mdi-plus</v-icon>
            New enhancement
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="loading">
        <v-col cols="12" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" />
        </v-col>
      </v-row>

      <v-row v-else-if="images.length === 0">
        <v-col cols="12">
          <v-card outlined class="text-center py-12">
            <v-icon size="48" color="primary">mdi-image-off-outline</v-icon>
            <p class="text-body-1 mt-3 mb-0">
              No images yet - upload one to get started.
            </p>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col
          v-for="image in images"
          :key="image.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card outlined class="image-card" @click="openImage(image)">
            <div class="image-thumb">
              <img
                v-if="thumbUrls[image.id]"
                :src="thumbUrls[image.id]"
                class="image-thumb-img"
                :alt="image.original_filename"
              />
              <div v-else class="image-thumb-placeholder">
                <v-progress-circular
                  v-if="loadingThumbs[image.id]"
                  indeterminate
                  size="24"
                  color="primary"
                />
                <v-icon v-else size="32" color="grey">mdi-image-outline</v-icon>
              </div>
              <v-chip
                small
                :color="statusColor(image.status)"
                class="image-status-chip"
                text-color="white"
              >
                {{ statusLabel(image.status) }}
              </v-chip>
            </div>
            <v-card-text>
              <div class="image-filename text-truncate" :title="image.original_filename">
                {{ image.original_filename }}
              </div>
              <div class="text-caption">
                {{ settingsSummary(image) }}
                <span v-if="image.credits_charged !== null">
                  &middot; {{ image.credits_charged }} credit{{ image.credits_charged === 1 ? "" : "s" }}
                </span>
              </div>
              <div v-if="image.status === 'failed' && image.error" class="text-caption error-text mt-1">
                {{ image.error }}
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="image.has_result"
                small
                text
                color="primary"
                :loading="downloading === image.id"
                @click.stop="downloadResult(image)"
              >
                <v-icon left small>mdi-download</v-icon>
                Download
              </v-btn>
              <v-btn
                v-if="['failed', 'needs_credits'].indexOf(image.status) !== -1"
                small
                text
                @click.stop="openImage(image, true)"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Retry
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn small icon color="error" @click.stop="confirmDelete(image)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title>Delete image?</v-card-title>
        <v-card-text>
          This permanently deletes
          <strong>{{ pendingDelete ? pendingDelete.original_filename : "" }}</strong>
          and its enhanced result. This can't be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="performDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { apiClient } from "@shared/services/api";
import { fetchFileBlob } from "@shared/utils/fileBlob";

const STATUS_META = {
  uploaded: { label: "Uploaded", color: "grey" },
  processing: { label: "Processing", color: "blue" },
  complete: { label: "Complete", color: "green" },
  failed: { label: "Failed", color: "red" },
  needs_credits: { label: "Needs credits", color: "orange" }
};

export default {
  name: "GalleryView",
  data() {
    return {
      images: [],
      loading: false,

      // Blob object URLs for authenticated thumbnails, keyed by image id -
      // <img> can't send an Authorization header, so each thumbnail is
      // fetched through the api service and rendered from an object URL.
      thumbUrls: {},
      loadingThumbs: {},

      downloading: null,

      deleteDialog: false,
      pendingDelete: null,
      deleting: false
    };
  },
  created() {
    this.fetchImages();
  },
  beforeDestroy() {
    Object.keys(this.thumbUrls).forEach(id => {
      if (this.thumbUrls[id]) URL.revokeObjectURL(this.thumbUrls[id]);
    });
  },
  methods: {
    statusLabel(status) {
      return (STATUS_META[status] && STATUS_META[status].label) || status;
    },
    statusColor(status) {
      return (STATUS_META[status] && STATUS_META[status].color) || "grey";
    },
    // Card click-through to the image's own result page; `retry: true`
    // (the card's Retry button) makes the result page restart the run.
    openImage(image, retry) {
      const route = { path: `/images/${image.id}` };
      if (retry) route.query = { retry: "1" };
      this.$router.push(route);
    },
    settingsSummary(image) {
      const parts = [];
      const settings = image.settings;
      const realism = this.realismLabel(settings);
      if (realism) parts.push(realism);
      parts.push(image.scale + "x");
      if (settings) {
        if (settings.output_format) {
          parts.push(settings.output_format === "jpeg" ? "JPEG" : "PNG");
        }
        if (settings.face_enhancement) parts.push("faces");
      }
      return parts.join(" · ");
    },
    realismLabel(settings) {
      if (!settings) return null;
      // Legacy rows stored a boolean realism_boost instead of a level.
      let level = settings.realism;
      if (!level && settings.realism_boost !== undefined) {
        level = settings.realism_boost ? "high" : "none";
      }
      const labels = {
        none: "Upscale only",
        basic: "Basic realism",
        high: "High realism",
        extreme: "Extreme realism"
      };
      return labels[level] || null;
    },

    async fetchImages() {
      this.loading = true;
      try {
        const response = await apiClient.get("/apps/upscale/images");
        this.images = (response.data && response.data.images) || [];
        this.images.forEach(image => this.loadThumbnail(image));
      } catch (e) {
        this.images = [];
      } finally {
        this.loading = false;
      }
    },

    async loadThumbnail(image) {
      // Prefer the upscaled result once available; fall back to the
      // original for images still uploading/processing/failed.
      const which = image.has_result ? "result" : "original";
      this.$set(this.loadingThumbs, image.id, true);
      try {
        const blob = await fetchFileBlob(apiClient,
          `/apps/upscale/images/${image.id}/file`,
          { params: { which } }
        );
        this.$set(this.thumbUrls, image.id, URL.createObjectURL(blob));
      } catch (e) {
        // No file yet (e.g. still uploaded, not processed) - leave the
        // placeholder icon in place.
      } finally {
        this.$set(this.loadingThumbs, image.id, false);
      }
    },

    async downloadResult(image) {
      this.downloading = image.id;
      try {
        const blob = await fetchFileBlob(apiClient,
          `/apps/upscale/images/${image.id}/file`,
          { params: { which: "result" } }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const stem = (image.original_filename || "image").replace(/\.[^.]+$/, "");
        const format =
          (image.settings && image.settings.output_format) || "png";
        a.download = stem + "_enhanced." + (format === "jpeg" ? "jpg" : "png");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        // Swallow - a missing/failed download is low-stakes here; the user
        // can simply try again.
      } finally {
        this.downloading = null;
      }
    },

    confirmDelete(image) {
      this.pendingDelete = image;
      this.deleteDialog = true;
    },
    async performDelete() {
      if (!this.pendingDelete) return;
      this.deleting = true;
      try {
        await apiClient.delete(`/apps/upscale/images/${this.pendingDelete.id}`);
        this.images = this.images.filter(img => img.id !== this.pendingDelete.id);
        if (this.thumbUrls[this.pendingDelete.id]) {
          URL.revokeObjectURL(this.thumbUrls[this.pendingDelete.id]);
          this.$delete(this.thumbUrls, this.pendingDelete.id);
        }
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.pendingDelete = null;
      }
    }
  }
};
</script>

<style scoped>
.gallery-subtitle {
  color: var(--text-secondary);
}

.image-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.image-thumb {
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--bg-secondary, #1b1b25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.image-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.image-status-chip {
  position: absolute;
  top: 6px;
  right: 6px;
}

.image-filename {
  font-weight: 600;
}

.error-text {
  color: var(--error, #f87171);
}
</style>
